import React from 'react';
import InAppNotificationCard, { OnInAppNotificationPress } from '../InAppNotificationCard';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { PRFlatList } from '../Scroll';
import { View } from 'react-native';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import { NotificationTypes } from '../../../primary-record-types';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import { getIntl } from '../../../utils/intl';
import { SearchBar, SelectFilter, FilterPopoverContent } from '../Filtering';
import FilterButton from '../Filtering/FilterButton';
import Divider from '../../v2BaseComponents/Divider';
import { colors } from '../../../utils/theme';
import { NotificationReadStatusFilterValue, NotificationSortOrder } from './useNotificationListState';
import EmptyStateHint from '../EmptyStateHint';

const intl = getIntl('inAppNotificationsList', {
    searchMessages: 'Search Messages',
    read: 'Read',
    unread: 'Unread',
    readStatus: 'Read Status',
    sortMessages: 'Sort Messages',
    newest: 'Newest',
    oldest: 'Oldest',
    date: 'Date',
});

export type InAppNotificationListOwnProps = {
    profileId: string;
    onNotificationPress: OnInAppNotificationPress;
    flat?: boolean;
    isSelectable?: boolean;
    onToggleSelect?: (inAppNotification: NotificationTypes.ReadOrUnreadInAppNotification) => void;
    getIsSelected?: (inAppNotification: NotificationTypes.ReadOrUnreadInAppNotification) => boolean;
    notifications: NotificationTypes.ReadOrUnreadInAppNotification[];
    isLoadingNotifications: boolean;
    searchText: string;
    onSearchTextChange: (value: string) => void;
    hasAnyProfileNotifications: boolean;
    readStatusFilterValue: NotificationReadStatusFilterValue;
    onReadStatusFilterValueChange: (value: NotificationReadStatusFilterValue) => void;
    sortOrder: NotificationSortOrder;
    onSortOrderChange: (value: NotificationSortOrder) => void;
    onMarkAsReadWhenNotificationPressed?: (inAppNotification: NotificationTypes.UnreadInAppNotification) => void;
};

export type InAppNotificationListProps = InAppNotificationListOwnProps;

const InAppNotificationList: React.FC<InAppNotificationListProps> = (props) => {
    const renderContent = () => {
        if (props.isLoadingNotifications) {
            return null;
        } else if (props.notifications.length) {
            return (
                <PRFlatList
                    key={props.notifications.length}
                    keyExtractor={(_) => JSON.stringify(_)}
                    style={{ flex: 1, alignSelf: 'stretch' }}
                    contentContainerStyle={{ padding: mainContentPadding }}
                    data={props.notifications}
                    renderItem={({ item }) => (
                        <InAppNotificationCard
                            key={JSON.stringify(item)}
                            inAppNotification={item}
                            medicalProfileId={props.profileId}
                            onPress={(medicalProfileId, inAppNotification) => {
                                props.onNotificationPress(medicalProfileId, inAppNotification);
                                if (!inAppNotification.read) {
                                    props.onMarkAsReadWhenNotificationPressed?.(inAppNotification);
                                }
                            }}
                            flat={props.flat}
                            isSelectable={props.isSelectable}
                            isSelected={props.getIsSelected?.(item)}
                            onToggleSelect={props.onToggleSelect}
                        />
                    )}
                    ItemSeparatorComponent={() => <View style={{ height: contentStackSpacing * 2 }} />}
                />
            );
        } else {
            return (
                <View style={{ flex: 1, alignSelf: 'stretch' }}>
                    <EmptyStateHint
                        variant={props.hasAnyProfileNotifications ? 'empty-notification-search' : 'empty-notifications'}
                    />
                </View>
            );
        }
    };

    return (
        <AnimateLoadedContent isLoading={props.isLoadingNotifications}>
            {() => (
                <Stack vertical style={{ flex: 1, alignSelf: 'stretch' }}>
                    <Stack
                        vertical
                        space={contentStackSpacing}
                        style={{ alignSelf: 'stretch', padding: mainContentPadding, backgroundColor: colors.White }}
                    >
                        <Stack horizontal space={2.5} align="center" fullWidth>
                            <Stack horizontal flex={1}>
                                <SearchBar
                                    placeholder={intl('searchMessages')}
                                    value={props.searchText}
                                    onChange={props.onSearchTextChange}
                                    isDisabled={props.isLoadingNotifications}
                                    testID="in-app-notification-search-bar"
                                />
                            </Stack>
                            <FilterButton
                                buttonIcon="VerticalArrows"
                                buttonTestID="messages-sort-button"
                                filterPromptTitle={intl('sortMessages')}
                                popoverTitle={intl('sortMessages')}
                                onResetFilters={() => props.onSortOrderChange('newest')}
                                renderFilterContents={() => (
                                    <FilterPopoverContent resetDisabled={props.sortOrder === 'newest'}>
                                        <SelectFilter
                                            onClear={() => props.onSortOrderChange('newest')}
                                            title={intl('date')}
                                            value={props.sortOrder}
                                            options={[
                                                { label: intl('newest'), value: 'newest' },
                                                { label: intl('oldest'), value: 'oldest' },
                                            ]}
                                            onChange={(sort) => {
                                                if (sort) props.onSortOrderChange(sort);
                                            }}
                                        />
                                    </FilterPopoverContent>
                                )}
                            />
                        </Stack>
                        <SelectFilter
                            value={props.readStatusFilterValue === 'all' ? null : props.readStatusFilterValue}
                            onChange={(value) => {
                                if (value === null) {
                                    props.onReadStatusFilterValueChange('all');
                                } else {
                                    props.onReadStatusFilterValueChange(value as NotificationReadStatusFilterValue);
                                }
                            }}
                            options={[
                                { label: intl('read'), value: 'read' },
                                { label: intl('unread'), value: 'unread' },
                            ]}
                            title={intl('readStatus')}
                        />
                    </Stack>
                    <Divider horizontal />
                    {renderContent()}
                </Stack>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(InAppNotificationList);
